import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { StorageMap } from "@ngx-pwa/local-storage";
import { Application, AuditLocation, Person, QuoteRequest, QuoteRequestLocation, QuoteRequestLocationStandard, QuoteRequestStandard, StandardsToApplication, UserProfile, webpages_Membership, webpages_Role, webpages_UsersInRole } from "app/shared/models/domain/data.model";
import { PageRequest } from "app/shared/models/request/page-request.model";
import { PageResponse } from "app/shared/models/response/page-response.model";
import { PublicClientCertificateView } from "app/shared/models/view/public-client-certificate-view";
import { environment } from "environments/environment";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { QuoteRequestStandardView, QuoteRequestView } from "../customers/apply/apply.types";
import { ApplicationBaseView, ApplicationContact, ApplicationFormView, ApplicationSearchView } from "./applications.types";

@Injectable({
    providedIn: 'root'
})
export class ApplicationsService {
    baseUrl = environment.apiBaseUrl;
    httpOptions: any = {
        withCredentials: true,
        observe: 'response' as 'response'
    };
    _openHelpSideDrawer: BehaviorSubject<any> = new BehaviorSubject(null);
    closedHelpSideDrawer: BehaviorSubject<boolean> = new BehaviorSubject(false);
    reInstate: BehaviorSubject<any> = new BehaviorSubject(null);
    private requestKey: string = "application-filter";

    constructor(
        private _httpClient: HttpClient,
        private storage: StorageMap
    ) {
    }

    // set openHelpSideDrawer$(value): Observable<any> {
    //     this._openHelpSideDrawer.next(value);
    // }

    get openHelpSideDrawer$(): Observable<any> {
        return this._openHelpSideDrawer.asObservable();
    }

    setApplicationStep(applicationId: number, step: number): void {
        let raw: string = null;
        if (step || step === 0) {
            raw = step.toString();
        }
        localStorage.setItem(`applicationStep.${applicationId}`, raw);
    }

    getApplicationStep(applicationId: number): number {
        const raw = localStorage.getItem(`applicationStep.${applicationId}`);
        return (raw) ? parseInt(raw) : null;
    }

    saveApplication(application: Application): Observable<any> {
        // return this._httpClient.put(this.baseUrl + 'api/data/application/token/' + application.applicationToken, application);

        if (application.applicationID) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/application/${application.applicationID}`, application).pipe(map((data) => data.item));
        } else {
            return this._httpClient.post<any>(`${this.baseUrl}api/data/application`, application).pipe(map((data) => data.item));
        }
    }

    initializaApplications(apps: Application[]) {
        var tasks = apps.map(app => this._httpClient.post<any>(this.baseUrl + 'api/data/application', app));

        return forkJoin(tasks);
    }

    saveStandards(standards: StandardsToApplication[]): Observable<any> {

        let tasks = standards.map((sta: StandardsToApplication) => {
            return this._httpClient.post(this.baseUrl + 'api/data/standard-to-application', sta);
        })

        return forkJoin(tasks);

    }

    completeQuoteRequest(requestId: string): Observable<QuoteRequest> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/quote-request/${requestId}/complete`, {}).pipe(map((data) => data.item));
    }

    completeQuoteRequestWithClient(requestId: string, isAdmin: boolean = true, auditId?: number): Observable<QuoteRequest> {
        const path = isAdmin ? '/admin' : '/public';
        return this._httpClient.post<any>(`${this.baseUrl}api/data${path}/quote-request/${requestId}/client-data-sync?auditId=${auditId}`, {}).pipe(map((data) => data.item));
    }

    saveQuoteRequest(request: QuoteRequest): Observable<QuoteRequest> {
        if (request.id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/admin/quote-request/${request.id}`, request).pipe(map((data) => data.item));
        } else {
            return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/quote-request`, request).pipe(map((data) => data.item));
        }
    }

    saveReadyForReview(request: QuoteRequest): Observable<QuoteRequest> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/admin/quote-request/${request.id}/complete`, request).pipe(map((data) => data.item));
    }

    savePublicQuoteRequest(request: QuoteRequest): Observable<QuoteRequest> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/quote-request`, request).pipe(map((data) => data.item));
    }

    updateAdminQuoteRequest(request: QuoteRequest, id: any, isAdmin: boolean = true): Observable<QuoteRequest> {
        const path = isAdmin ? '/admin' : '/public';
        if (id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data${path}/quote-request/${id}`, request).pipe(map((data) => data.item));
        }
        if (!request?.clientId) {
            request.clientId = 0;
        }
        return this._httpClient.post<any>(`${this.baseUrl}api/data${path}/quote-request`, request).pipe(map((data) => data.item));
    }

    saveAdminQuoteRequest(request: QuoteRequest): Observable<QuoteRequest> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/quote-request`, request).pipe(map((data) => data.item));
    }

    saveLocation(location: QuoteRequestLocation, isPublic: boolean = false): Observable<QuoteRequestLocation> {
        const path = !isPublic ? '/' : '/public/';
        if (location.id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data${path}quote-request-location/${location.id}`, location).pipe(map((data) => data.item));
        }
        return this._httpClient.post<any>(`${this.baseUrl}api/data${path}quote-request-location`, location).pipe(map((data) => data.item));
    }

    saveLocationForClient(location: QuoteRequestLocation): Observable<QuoteRequestLocation> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/clients/update-client-quote-request-location-details`, location).pipe(map((data) => data.item));
    }

    saveLocationPublic(location: QuoteRequestLocation): Observable<QuoteRequestLocation> {
        if (location.id) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/public/quote-request-location/${location.id}`, location).pipe(map((data) => data.item));
        }
        return this._httpClient.post<any>(`${this.baseUrl}api/data/public/quote-request-location`, location).pipe(map((data) => data.item));
    }

    saveAuditLocationPublic(location: AuditLocation): Observable<AuditLocation> {
        if (location.auditLocationId) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/public/audit-locations/${location.auditLocationId}`, location).pipe(map((data) => data.item));
        }
        return this._httpClient.post<any>(`${this.baseUrl}api/data/public/audit-locations`, location).pipe(map((data) => data.item));
    }

    removeLocation(locationId: string): Observable<QuoteRequestLocation> {
        return this._httpClient.delete<any>(`${this.baseUrl}api/data/quote-request-location/${locationId}`).pipe(map((data) => data.item));
    }

    saveStandard(standard: QuoteRequestStandard, isAdmin: boolean = true): Observable<QuoteRequestStandard> {
        const path = isAdmin ? '/data' : '/data/public';
        if (standard.selectedStandardId) {
            return this._httpClient.put<any>(`${this.baseUrl}api${path}/quote-request-standard/${standard.selectedStandardId}`, standard).pipe(map((data) => data.item));
        }
        return this._httpClient.post<any>(`${this.baseUrl}api${path}/quote-request-standard`, standard).pipe(map((data) => data.item));
    }

    saveLocationStandard(standard: QuoteRequestLocationStandard): Observable<QuoteRequestLocationStandard> {
        if (standard.selectedStandardId) {
            return this._httpClient.put<any>(`${this.baseUrl}api/data/quote-request-location-standard/${standard.selectedStandardId}`, standard).pipe(map((data) => data.item));
        } else {
            return this._httpClient.post<any>(`${this.baseUrl}api/data/quote-request-location-standard`, standard).pipe(map((data) => data.item));
        }
        return null;
    }
    removeStandard(standardId: string, isAdmin: boolean = true): Observable<any> {
        const path = isAdmin ? '/data' : '/data/public';
        return this._httpClient.delete<any>(`${this.baseUrl}api${path}/quote-request-standard/${standardId}`).pipe(map((data) => data.item));
    }

    getQuoteRequestApplications(requestId: string): Observable<Application[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/application`, {
            params: {
                'find[ApplicationToken]': requestId
            }
        }).pipe(map((data) => {
            return data.items as Application[]
        }));

    }

    GetClientCertificateDetails(CertificateNumber: string): Observable<PublicClientCertificateView> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/view/download-client-certificate`, {
            params: {
                'find[CertificateNumber]': CertificateNumber
            }
        }).pipe(map((data) => {
            return data?.items[0] as PublicClientCertificateView
        }));
    }

    getQuoteRequestStandards(requestId: string, isAdmin: boolean = true): Observable<QuoteRequestStandardView[]> {
        const path = isAdmin ? '' : '/public';
        return this._httpClient.get<any>(`${this.baseUrl}api/data${path}/quote-request-standard`, {
            params: {
                'find[QuoteRequestId]': requestId
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestStandardView[]
        }));

    }

    getQuoteRequestLocations(requestId: string, isAdmin: boolean = true): Observable<QuoteRequestLocation[]> {
        const path = isAdmin ? '' : '/public';
        if (!requestId) {
            return of(null);
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data${path}/quote-request-location`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'sort[Created]': 'asc',
                'itemsPerPage': 1000
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    getQuoteRequestLocationsPublic(requestId: string): Observable<QuoteRequestLocation[]> {
        if (!requestId) {
            return of(null);
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data/public/quote-request-location`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'sort[Created]': 'asc',
                'itemsPerPage': 1000
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    getAuditLocationsPublic(requestId: string): Observable<AuditLocation[]> {
        if (!requestId) {
            return of(null);
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data/public/audit-locations`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'sort[Created]': 'asc',
                'itemsPerPage': 1000
            }
        }).pipe(map((data) => {
            return data.items as AuditLocation[]
        }));

    }

    getQuoteRequestLocationIds(requestId: string[]): Observable<QuoteRequestLocation[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-location`, {
            params: {
                'find[isMainSite]': false,
                // 'find[clientAdditionalSiteId]': 'IS NOT NULL',
                'find[QuoteRequestId:in]': requestId.join(),
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    getQuoteRequestLocationById(id: string): Observable<QuoteRequestLocation> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-location/${id}`, {}).pipe(map((data) => {
            return data.item as QuoteRequestLocation
        }));
    }

    getQuoteRequestLocationsForUniqueSiteName(requestId: string, name: string, id: string): Observable<QuoteRequestLocation[]> {
        if (!requestId) {
            return of(null);
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-location`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'find[SiteName]': name,
                // 'find[Id:notin]': [id],
                'sort[Created]': 'asc',
                'itemsPerPage': 1000
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    getQuoteRequestLocationsForUniqueSiteNamePublic(requestId: string, name: string, id: string): Observable<QuoteRequestLocation[]> {
        if (!requestId) {
            return of(null);
        }
        return this._httpClient.get<any>(`${this.baseUrl}api/data/public/quote-request-location`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'find[SiteName]': name,
                // 'find[Id:notin]': [id],
                'sort[Created]': 'asc',
                'itemsPerPage': 1000
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    getAdminQuoteRequests(clientId): Observable<QuoteRequest[]> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/admin/quote-request?itemsPerPage=1000&find[clientId]=' + clientId + '&sort[created]=desc').pipe(map((data) => data.items));
    }

    getAdminQuoteRequest(requestId: string, isAdmin: boolean = true): Observable<QuoteRequestView> {
        const path = isAdmin ? '/admin' : '/public';
        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/data'+path+'/quote-request/' + requestId).pipe(map((data) => data.item as QuoteRequest))
            , this._httpClient.get<any>(this.baseUrl + 'api/public/options/get-options')
                .pipe(map((data) => {
                    data.item.standardList = data.item.standardList.sort((x, y) => {
                        if (x.label < y.label) { return -1; }
                        if (x.label > y.label) { return 1; }
                        return 0;
                    });
                    return data.item;
                }))
            , this.getQuoteRequestStandards(requestId, isAdmin)
            , this.getQuoteRequestLocations(requestId, isAdmin)
        ]).pipe(
            map(([quoteRequest, options, standards, locations]: [QuoteRequest, any, QuoteRequestStandardView[], QuoteRequestLocation[]]) => {

                let idx: number = -1;

                if (locations?.length) {
                    idx = locations.findIndex(l => true === l.isMainSite);
                }

                let mainSite: QuoteRequestLocation = null;
                let otherSites: QuoteRequestLocation[] = null;

                if (idx > -1) {
                    mainSite = locations[idx];
                    if (locations.length > 1) {
                        otherSites = JSON.parse(JSON.stringify(locations));
                        otherSites.splice(idx, 1);
                    }
                }

                return {
                    quoteRequest: quoteRequest
                    , standards: standards.map((standard) => {
                        standard.label = options.standardList.filter(s => s.value == standard.standardId.toString())[0]?.label
                        return standard;
                    })
                    , options: options
                    , mainSite: mainSite
                    , otherSites: otherSites
                } as QuoteRequestView;
            })
        )
    }

    getApplicationViewByID(id: string): Observable<ApplicationFormView> {
        return forkJoin([
            this._httpClient.get<any>(this.baseUrl + 'api/data/application/' + id).pipe(map((data) => data.item))
            , this._httpClient.get<any>(this.baseUrl + 'api/public/options/get-options').pipe(map((data) => data.item))
        ]).pipe(
            map(([application, options]: [Application, any]) => {
                return {
                    application: application
                    , standardAppliedFor: options.standardList.filter(s => s.value == application.standardID)[0]
                    , options: options
                } as ApplicationFormView;
            })
        )
    }


    getStandardsByApplicationId(id: any): Observable<any> {
        return this._httpClient.get<any>(this.baseUrl + 'api/data/standard-to-application?find[applicationID]=' + id)
            .pipe(map((data) => data.items));
    }

    getIAFCodesByApplicationId(id: any): Observable<any> {
        id = id?.toString();
        if (id) {
            return this._httpClient.get<any>(this.baseUrl + 'api/data/application-industry-code?find[quoteRequestId]=' + id)
                .pipe(map((data) => data.items));
        }
        return of([]);
    }

    getIAFCodesByApplicationIdPublic(id: any): Observable<any> {
        id = id?.toString();
        if (id) {
            return this._httpClient.get<any>(this.baseUrl + 'api/data/public/application-industry-code?itemsPerPage=1000&find[quoteRequestId]=' + id)
                .pipe(map((data) => data.items));
        }
        return of([]);
    }

    saveIAFCodesByApplicationId(id: any, codeId): Observable<any> {
        id = id?.toString();
        const params = { applicationIndustryCodeId: id, quoteRequestId: id, codeId: codeId };
        return this._httpClient.post(this.baseUrl + 'api/data/application-industry-code', params);
    }

    deleteIAFCodesByApplicationId(id: any): Observable<any> {
        return this._httpClient.delete(this.baseUrl + 'api/data/application-industry-code/' + id);
    }

    sendApplicationToCustomer(quoteRequestId, email = ''): Observable<any> {
        if (quoteRequestId && !email) {
            return this._httpClient.put(this.baseUrl + 'api/data/quote-request/send/' + quoteRequestId, {}).pipe(map((data: any) => data));
        }
        return this._httpClient.post(this.baseUrl + 'api/data/quote-request/send/public/' + email, {}).pipe(map((data: any) => data));
    }

    sendApplication(profiles: UserProfile[], applicationData): Observable<any> {
        if (profiles && profiles.length) {
            const profileTasks: Observable<UserProfile>[] = profiles.map((profile: UserProfile) => {
                if (profile.userID > 0) {
                    return this._httpClient.get(`${this.baseUrl}api/data/user-profile/${profile.userID}`).pipe(map((data: PageResponse<UserProfile>) => data.item));
                } else {
                    return forkJoin([
                        this._httpClient.get(`${this.baseUrl}api/data/admin/roles?find[RoleName]=Customer`).pipe(map((data: PageResponse<webpages_Role>) => data.items[0]))
                        , this._httpClient.post(`${this.baseUrl}api/data/user-profile`, profile).pipe(map((data: PageResponse<UserProfile>) => data.item))
                    ]).pipe(
                        switchMap(([role, newProfile]: [webpages_Role, UserProfile]) => {
                            return forkJoin([
                                this._httpClient.post(`${this.baseUrl}api/data/member`, { userId: newProfile.userID }).pipe(map((data: PageResponse<webpages_Membership>) => {
                                    return data.item;
                                }))
                                , this._httpClient.post(`${this.baseUrl}api/data/admin/user-roles/${newProfile.userID}/${role.roleID}`, {})
                            ]).pipe(switchMap((data) => {
                                return this._httpClient.get(`${this.baseUrl}api/data/user-profile/${newProfile.userID}`).pipe(map((data: PageResponse<UserProfile>) => data.item));
                            }));
                        }));
                }
            });
            return forkJoin([
                forkJoin(profileTasks)
                , this._httpClient.post(`${this.baseUrl}api/data/application`, applicationData).pipe(map((data: PageResponse<Application>) => data.item))])
                .pipe(
                    switchMap(([profilesData, application]: [UserProfile[], Application]) => {
                        const userAppTasks = profilesData.map((up: UserProfile) => {
                            return this._httpClient.post(`${this.baseUrl}api/data/application-user`, { applicationId: application.applicationID, userId: up.userID });
                        });
                        return forkJoin(userAppTasks)
                            .pipe(switchMap(() => {

                                return this._httpClient.post(this.baseUrl + 'api/data/application/send/' + application.applicationID, {}).pipe(map((data: any) => application));
                                // return application;
                            }));
                    })
                );
        }
    }

    getParams(request, params): any {
        params['itemsPerPage'] = request.itemsPerPage?.toString();
        params['currentPage'] = request.currentPage?.toString();
        if (request.sort && request.sortDir) {

            if (Object.keys(this.sortMap).indexOf(request.sort) > -1) {
                params[`sort[${this.sortMap[request.sort]}]`] = request.sortDir;
            }
            else {
                params[`sort[${request.sort}]`] = request.sortDir;
            }
        }

        if (request.salesRepID) {
            params['find[createdBy]'] = request.salesRepID;
        }

        if (request.applicationStatusID) {
            params['find[applicationStatusID]'] = request.applicationStatusID;
        }

        if (request.searchParam) {
            params['find[details_Scope]'] = request.searchParam;
        }

        if (request.standardID) {
            params['find[standardID]'] = request.standardID;
        }

        if (request.clientID) {
            params['find[clientID]'] = request.clientID;
        }

        if (request.clientId) {
            params['find[clientId]'] = request.clientId;
        }


        if (request.salesRep) {
            params['orLike[assignedToUserId]'] = parseInt(request.salesRep);
        }

        if (request.requestStatus) {
            params['find[requestStatus]'] = request.requestStatus;
        }

        if (request.clientID) {
            params['orLike[clientId]'] = request.clientID;
            params['orLike[clientID]'] = request.clientID;
        }

        if (request.userID) {
            params['orLike[userID]'] = request.userID;
        }

        if (request.assignedToUserId) {
            params['orLike[assignedToUserId]'] = request.assignedToUserId;
            params['orLike[assignedByUserId]'] = request.assignedToUserId;
            params['orLike[createdByUserId]'] = request.assignedToUserId;
        }

        if (request.showUnassignedOnly && request.showUnassignedOnly !== '') {
            params['find[assignedTo]'] = '';
        }

        if (request.query) {
            params['orLike[companyName]'] = request.query;
        }

        if (request.applicationId) {
            params['find[applicationId]'] = request.applicationId;
        }

        return params;
    }

    getApplicationLegacyViewByID(applicationId: number): Observable<ApplicationBaseView> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/application/legacy/view/${applicationId}`, { params: null })
            .pipe(map((data) => {
                if (data && data.item) {
                    return data.item
                }
                return null;
            }));
    }

    getLegacyApplications(request: any): Observable<PageResponse<ApplicationBaseView>> {
        let params = {};

        params = this.getParams(request, params);

        return this._httpClient.get<any>(`${this.baseUrl}api/data/application/legacy/view`, { params: params })
            .pipe(map((data) => {
                return data as PageResponse<ApplicationBaseView>;
            }));
    }

    deleteLegacyApplication(applicationId: number): Observable<Application> {
        return this._httpClient.delete<Application>(`${this.baseUrl}api/data/application/legacy/${applicationId}`);
    }


    getApplications(request: any): Observable<PageResponse<ApplicationBaseView>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/application/view`, { params: params })
            .pipe(map((data: any) => data as PageResponse<ApplicationBaseView>));
    }

    deleteQuoteRequest(quoteRequestId: string): Observable<QuoteRequest> {
        return this._httpClient.delete<QuoteRequest>(`${this.baseUrl}api/data/admin/quote-request/${quoteRequestId}`);
    }

    getLocal(): Observable<any> {
        return this.storage.get(this.requestKey)
            .pipe(map(data => new PageRequest().deserialize(data)));
    }

    saveLocal(model: PageRequest): Observable<any> {
        return this.storage.set(this.requestKey, model);
    }

    deleteLocal(): Observable<any> {
        return this.storage.delete(this.requestKey);
    }

    getLocationByQuoteRequestId(requestId: string): Observable<QuoteRequestLocation> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-location`, {
            params: {
                'find[QuoteRequestId]': requestId,
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            if (data && data.items && data.items.length) {
                return data.items[0] as QuoteRequestLocation;
            }
            return null;
        }));
    }

    saveApplicationUpgrade(request: any): Observable<any> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/application/legacy/upgrade`, request).pipe(map((data: any) => data.item));
    }

    getAttachments(request: any): Observable<PageResponse<any>> {
        let params = {};
        params = this.getParams(request, params);
        return this._httpClient.get<any>(`${this.baseUrl}api/data/application-attachment`, { params: params })
            .pipe(map((data) => {
                return data as PageResponse<any>;
            }));
    }

    saveAuditAuditorsActivity(auditId, quoteRequestId): Observable<PageResponse<any>> {
        return this._httpClient.post<any>(`${this.baseUrl}api/data/public/quote-request/save-audit-auditor-history/${auditId}/${quoteRequestId}`, {})
            .pipe(map((data) => {
                return data as PageResponse<any>;
            }));
    }

    getQuoteRequestLocationIdsByAdditionalSiteId(requestId): Observable<QuoteRequestLocation[]> {
        return this._httpClient.get<any>(`${this.baseUrl}api/data/quote-request-location`, {
            params: {
                'find[isMainSite]': false,
                'find[clientAdditionalSiteId:in]': requestId.join(),
                'sort[Created]': 'asc',
            }
        }).pipe(map((data) => {
            return data.items as QuoteRequestLocation[]
        }));

    }

    get reInstate$(): Observable<any> {
        return this.reInstate.asObservable();
    }

    setReInstate(value): void {
        this.reInstate.next(value);
    }

    private sortMap: any = {
        created: 'createdDate'
        , status: 'applicationStatusID'
        , standard: 'standardID'
        , scope: 'details_Scope'
    };
}
